import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {connect} from 'react-redux';

import ClientTimestamp from 'app/components/common/client-timestamp';
import CurrencyInput   from 'app/components/common/currency-input';
import Icon            from 'app/components/common/icon';
import FileInput       from 'app/components/common/file-input';
import RteEditor       from 'app/components/common/rich-text/editor';
import RteViewer       from 'app/components/common/rich-text/viewer';
import FileChips       from 'app/components/company-admin/grant-system/file-chips';
import RubricForm      from 'app/components/company-admin/grant-system/rubric/form';
import RubricScore     from 'app/components/company-admin/grant-system/rubric/score';
import GrantSysDuck    from 'app/ducks/company-admin/grant-system';
import helpers         from 'app/helpers/grant-system';
import prompts         from 'app/prompts';
import AuthSlx         from 'app/selectors/auth';

const {FieldPresenceTypes, TaskTypes} = helpers;

class GrantAssignmentForm extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      remarks: '',
      files: [],
      score: null,
      rubComplete: false,
      amountAwarded: null,
    };

    this.onChangeRemarks = this.onChangeRemarks.bind(this);
    this.onChangeFiles = this.onChangeFiles.bind(this);
    this.onChangeRubricScore = this.onChangeRubricScore.bind(this);
    this.onChangeAmountAwarded = this.onChangeAmountAwarded.bind(this);
    this.onClickSubmit = this.onClickSubmit.bind(this);
  }

  get saveAttrs() {
    const {remarks, files, score, rubComplete, amountAwarded} = this.state;
    const attrs = {
      remarks: (remarks || '').trim() || null,
      files: files.length ? files : null,
      score: rubComplete ? score : null,
      amountAwarded: (this.requireAmount ? amountAwarded : null),
    };
    return attrs;
  }

  get canSubmit() {
    const {saveAttrs, requireAmount, isOwn} = this;
    const {task, submitPending} = this.props;
    const {amountAwarded} = this.state;
    if (!isOwn) return false;
    if (submitPending) return false;
    if (task.remarksField === FieldPresenceTypes.REQUIRED && !saveAttrs.remarks) return false;
    if (task.fileField    === FieldPresenceTypes.REQUIRED && !saveAttrs.files?.length) return false;
    if (task.rubric && !saveAttrs.score) return false;
    if (requireAmount) {
      if (!_.isFinite(amountAwarded)) return false;
      if (amountAwarded < 0) return false;
    }
    return true;
  }

  get typeObj() {
    const {task} = this.props;
    if (!task) return null;
    return helpers.taskTypeObj(task);
  }
  get isFinal() {
    return this.typeObj.key === TaskTypes.FINAL;
  }

  get requireAmount() {
    if (!this.isFinal) return false;
    return this.state.score?.absolute > 0;
  }

  get isComplete() {
    return !!this.props.ass.completedTs;
  }

  get isOwn() {
    const {ass, currentUser} = this.props;
    return ass.employeeId === currentUser.employment?.employee?.id;
  }

  onChangeRemarks(md) {
    this.setState({remarks: md});
  }
  onChangeFiles(files) {
    this.setState({files});
  }
  onChangeRubricScore(rubComplete, score) {
    this.setState({rubComplete, score});
  }
  onChangeAmountAwarded({amount}) {
    this.setState({amountAwarded: amount});
  }

  async onClickSubmit() {
    const msg = `This can not be undone.`;
    const didConfirm = await prompts.confirm({msg, confirmBtnLabel: 'Submit'});
    if (didConfirm) {
      this.props.submit(this.saveAttrs);
    }
  }

  renderComment() {
    const {task} = this.props;
    if (task.remarksField === FieldPresenceTypes.OFF) return null;
    const isRequired = task.remarksField === FieldPresenceTypes.REQUIRED;

    return (
      <div className="gass-form-field">
        <label className="gass-form-field-label">Internal Remarks{isRequired ? '*' : ''}</label>
        <RteEditor initialMd={this.state.remarks} onChange={this.onChangeRemarks} />
      </div>
    );
  }
  renderFile() {
    const {task} = this.props;
    if (task.fileField === FieldPresenceTypes.OFF) return null;
    const isRequired = task.fileField === FieldPresenceTypes.REQUIRED;

    return (
      <div className="gass-form-field">
        <label className="gass-form-field-label">Attachments{isRequired ? '*' : ''}</label>
        <FileInput files={this.state.files} onChange={this.onChangeFiles} maxFiles={10} onGray ghostBtn />
      </div>
    );
  }
  renderRubric() {
    const {rubric} = this.props.task;
    if (!rubric?.crits?.length) return null;
    const hasMulti = rubric.crits.length > 1;

    return (
      <div className="gass-form-field">
        {hasMulti && <label className="gass-form-field-label">Score each criterion*</label>}
        <RubricForm rubric={rubric} onChange={this.onChangeRubricScore} />
      </div>
    );
  }
  renderAmount() {
    if (!this.requireAmount) return null;

    return (
      <div className="gass-form-field">
        <label className="gass-form-field-label">Award Amount</label>
        <CurrencyInput currencyCode="USD" onChange={this.onChangeAmountAwarded} amount={this.state.amountAwarded} allowNull />
      </div>
    );
  }

  renderSubmit() {
    const {task, submitPending} = this.props;
    const {saveAttrs, canSubmit, typeObj} = this;
    return (
      <div className="_mt-48 _flex _justify-end">
        <button className="btn blue" disabled={!canSubmit} onClick={this.onClickSubmit}>{submitPending ? 'Submitting...' : `Submit`}</button>
      </div>
    );
  }

  renderComplete() {
    const {ass, task} = this.props;
    const hasFiles = !!ass.files?.length;
    return (<>
      {!!ass.score && (
        <RubricScore score={ass.score} className="gass-form-res-score" />
      )}
      {!!ass.remarks && (
        <RteViewer md={ass.remarks} fontSize="large" className="gass-form-res-remarks" />
      )}
      {hasFiles && (
        <FileChips files={ass.files} className="gass-form-res-file-chips" />
      )}
    </>);
  }

  renderIncomplete() {
    const {task} = this.props;
    return (<>
      <RteViewer md={task.description || ''} fontSize="large" className="gass-form-desc" />
      {this.renderRubric()}
      {this.renderAmount()}
      {this.renderComment()}
      {this.renderFile()}
      {this.renderSubmit()}
    </>);
  }

  renderAssignee() {
    const {ass, currentUser} = this.props;
    const {employee} = ass;
    const {isComplete, isOwn} = this;
    const line1 = isComplete ? 'Submitted' : (isOwn ? 'Assigned to You' : 'Assigned to');
    const showDetails = isComplete || !isOwn;
    const empName = employee ? `${employee.firstName} ${employee.lastName}` : 'Someone';

    return (<>
      <span className="_upper">{line1}</span>
      {showDetails && (<>
        <br />
        {empName}
        {isComplete && <>&nbsp;| <ClientTimestamp timestamp={ass.completedTs} format="MMM D, YYYY" sameYearFormat="MMMM D" /></>}
      </>)}
    </>);
  }

  render() {
    const {app, ass, task, stage} = this.props;
    if (!task) return null;
    const {typeObj} = this;
    const TypeIcon = typeObj.IconComp;

    return (
      <div className={`gass-form`}>
        <div className="gass-form-top _flex _justify-between">
          <div className="gtask-type-chip blue"><TypeIcon />{typeObj.label}</div>
          <div className="_clr-blue _ta-right">
            {this.renderAssignee()}
          </div>
        </div>
        <div className="gass-form-head">
          <h1 className="gass-form-head-title">{task.title}</h1>
        </div>

        {this.isComplete
          ? this.renderComplete()
          : this.renderIncomplete()
        }
        {/* <pre>{JSON.stringify({saveAttrs: this.saveAttrs}, null, 4)}</pre> */}
      </div>
    );
  }

}

GrantAssignmentForm.propTypes = {
  app: PropTypes.object.isRequired,
  company: PropTypes.object.isRequired,
  stage: PropTypes.object.isRequired,
  task: PropTypes.object.isRequired,
  ass: PropTypes.object.isRequired,
};

GrantAssignmentForm.defaultProps = {
};

const stateToProps = (state) => ({
  currentUser: AuthSlx.currentUser(state),
  submitPending: GrantSysDuck.Slx.assCompletePending(state),
});

const dispatchToProps = (dispatch, ownProps) => ({
  submit: (params) => dispatch(GrantSysDuck.Ax.myAssignmentsComplete(ownProps.ass.id, params)),
});

export default connect(stateToProps, dispatchToProps)(GrantAssignmentForm);
